import { AfterViewInit, Component, Inject, ElementRef, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatDialogRef, MAT_DIALOG_DATA, MatTableDataSource, MatTable } from '@angular/material';
import { MatPaginator } from '@angular/material/paginator';

import { Subscription } from 'rxjs';
import { takeUntil, distinctUntilChanged, debounceTime } from 'rxjs/operators';

import { UserSearchResult, PersonInfo, CategoryInfo, OutOfOfficeSetting } from 'services/application/application.service';
import { ConfigService } from 'services/config/config.service';
import { ErrorMessageService } from 'services/errormsg/errormsg.service';
import { ScreenSizeService } from 'services/screensize/screensize.service';
import { SplashService } from 'services/splash/splash.service';
import { UserService } from 'services/user/user.service';

import { BaseDialogOptions } from '../dialogoptions';

@Component({
    selector: 'dialog-ooodesigneesearch-component',
    styleUrls: ['ooodesigneesearch.dialogcomponent.scss'],
    templateUrl: 'ooodesigneesearch.dialogcomponent.html',
})
export class OutOfOfficeDesigneeSearchDialogComponent implements AfterViewInit, OnInit, OnDestroy {
    @ViewChild('lastname') LastnameInput: ElementRef;
    @ViewChild('datebegininput') DateBeginInput: ElementRef;
    @ViewChild('scrollpanel') ScrollPanel: ElementRef;
    @ViewChild(MatPaginator) paginator: MatPaginator;

    private _subrelease: Subscription = new Subscription();

    get SelectionTypeInfo() {
        return <FormGroup>this.SearchForm.get('SelectionTypeInfo') || <FormGroup>{};
    }
    get RecipientInfo() {
        return <FormGroup>this.SearchForm.get('RecipientInfo') || <FormGroup>{};
    }
    get DatesInfo() {
        return <FormGroup>this.SearchForm.get('DatesInfo') || <FormGroup>{};
    }
    get CategoriesInfo() {
        return <FormArray>this.SearchForm.get('CategoriesInfo') || <FormArray>{};
    }
    get CurrentStep() {
        return <FormControl>this.SearchForm.get('CurrentStep') || <FormControl>{};
    }

    title: string;
    message: string;
    oktext: string;
    canceltext: string;
    categories: CategoryInfo[];
    showIcon: boolean; // TODO!
    subordinates: PersonInfo[];

    AllDistrictHeaders: string[] = ['LastName', 'FirstName', 'LocalJobTitle', 'Actions'];
    AllSteps: StepButtonInfo[];
    ConfirmationIsUp: boolean;
    InitialValues: OutOfOfficeSetting;
    IsUpdate: boolean;
    NoData: boolean;
    ResultsData: MatTableDataSource<UserSearchResult>;
    SearchForm: FormGroup;
    SmallDisplay: boolean;
    UserSearchConducted: boolean;
    SearchParams: FormGroup;
    SearchResults: UserSearchResult[];
    VisibleColumns: string[];
    VisibleHeaders: string[];

    constructor(
        private dialogRef: MatDialogRef<OutOfOfficeDesigneeSearchDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private data: BaseOOODesigneeSearchDialogOptions,
        private configSvc: ConfigService,
        private errorSvc: ErrorMessageService,
        private fb: FormBuilder,
        private http: HttpClient,
        private screenSizeSvc: ScreenSizeService,
        private splashSvc: SplashService,
        private userSvc: UserService
    ) {
        this.title = data.TitleText || 'Information';
        this.message = data.MessageText || '';
        this.oktext = data.OKText || 'OK';
        this.canceltext = data.CancelText || 'Cancel';
        this.categories = data.Categories || [];
        this.InitialValues = data.InitialValues;
        this.IsUpdate = !!this.InitialValues;
        this.subordinates = data.Subordinates || [];
    }

    ngOnDestroy() {
        this._subrelease.unsubscribe();
    }

    ngAfterViewInit() {
        if (this.paginator) {
            this.ResultsData.paginator = this.paginator;
        }
    }

    ngOnInit() {
        const self = this;

        self.NoData = true;
        self.SearchResults = [];
        self.ResultsData = new MatTableDataSource<UserSearchResult>([<UserSearchResult>{}]);

        // Begin setting up the search form. If we have initial values provided, use those to set initial values in various fields.
        const ivCategories = self.categories.map(c => {
            return {
                CategoryID: c.CategoryID,
                CategoryDescription: c.CategoryDescription,
                Selected: false,
            };
        });

        let ivMode = '';
        let ivUser = self.InitialValues
            ? ({
                  FirstName: self.InitialValues.DesigneeFirstName,
                  LastName: self.InitialValues.DesigneeLastName,
                  SAPID: self.InitialValues.DesigneeSAPID,
                  LocalJobTitle: self.InitialValues.DesigneeLocalJobTitle,
              } as PersonInfo)
            : null;

        if (self.InitialValues != null) {
            if (self.InitialValues.Categories && self.InitialValues.Categories.length) {
                // For each category passed in, we need to turn the Selected field to true.
                self.InitialValues.Categories.forEach(c => {
                    const foundEntry = ivCategories.find(t => t.CategoryID === c.CategoryID);

                    if (foundEntry) {
                        foundEntry.Selected = true;
                    }
                });
            }

            if (ivUser) {
                // If the user provided is in the list of subordinates, we can initialize the User field
                // accordingly.
                const foundUser = self.subordinates.find(s => s.SAPID === ivUser.SAPID);

                if (foundUser) {
                    ivMode = 'subordinate';
                    ivUser = foundUser;
                }
            }
        }

        self.SearchForm = self.fb.group({
            CurrentStep: [0],
            RecipientInfo: self.fb.group({
                User: [ivUser, Validators.required],
                SAPID: [(ivUser && ivUser.SAPID) || '', [Validators.required, Validators.pattern(/^\d{2,8}$/)]],
            }),
            DatesInfo: self.fb.group({
                DateBegin: [self.InitialValues && self.InitialValues.DateBegin, Validators.required],
                DateEnd: [self.InitialValues && self.InitialValues.DateEnd],
                Active: [self.InitialValues != null && self.InitialValues.Active != null ? self.InitialValues.Active : true],
            }),
            SelectionTypeInfo: self.fb.group({
                Type: [ivMode, Validators.required],
            }),
            CategoriesInfo: self.fb.array(
                ivCategories.map(c =>
                    self.fb.group({
                        CategoryID: c.CategoryID,
                        Description: c.CategoryDescription,
                        Selected: [c.Selected],
                    })
                ),
                (farr: FormArray) => {
                    const farrval = farr.value as any[];

                    return farrval.findIndex(f => f.Selected === true) < 0
                        ? {
                              NoneSelected: true,
                          }
                        : null;
                }
            ),
            Username: [null],
        });
        self.SearchParams = self.fb.group(
            {
                FirstName: [null],
                LastName: [null],
                SAPID: [null, Validators.pattern(/^\d+$/)],
            },
            {
                validators: frm => {
                    const frmval = frm.value;

                    return !frmval.FirstName && !frmval.LastName && !frmval.SAPID
                        ? {
                              NoSearchParameters: true,
                          }
                        : null;
                },
            }
        );
        self.UserSearchConducted = false;

        self.AllSteps = [
            { Text: 'Search Method', Active: true, Visited: true, Valid: true },
            { Text: 'Select Designee', Active: false, Visited: false, Valid: false },
            { Text: 'Select Dates', Active: false, Visited: false, Valid: false },
            { Text: 'Select Categories', Active: false, Visited: false, Valid: false },
            { Text: 'Final Confirmation', Active: false, Visited: false, Valid: false },
        ];

        // Subscribe to changes on the search form's SelectionType element, so we can maintain requirements on the elements needed.
        self._subrelease.add(
            self.RecipientInfo.get('User')
                .valueChanges.pipe(
                    distinctUntilChanged(),
                    debounceTime(250)
                )
                .subscribe(value => {
                    self.RecipientInfo.get('SAPID').setValue(value && value.SAPID, { emitEvent: false });
                })
        );

        self._subrelease.add(
            self.SearchForm.valueChanges
                .pipe(
                    distinctUntilChanged(),
                    debounceTime(250)
                )
                .subscribe(value => {
                    if (value) {
                        // User made a selection. Update the buttons.
                        self.CalculateButtonValidity();
                    }
                })
        );

        // Watch for screen size updates
        self._subrelease.add(
            self.screenSizeSvc.ScreenSizeSub.subscribe(size => {
                self.SmallDisplay = size.IsSmallSize;
                self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllDistrictHeaders.slice();
                self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();
            })
        );

        // First time size initialization
        self.SmallDisplay = self.screenSizeSvc.ScreenSize.IsSmallSize;

        self.CalculateButtonValidity();
    }

    Cancel() {
        this.dialogRef.close(null);
    }

    // #region Calculate Button Validity

    CalculateButtonValidity() {
        const self = this;
        const curStep = self.CurrentStep.value;

        self.AllSteps.forEach((btn, idx) => {
            switch (idx) {
                case 1: // Recipient. Valid once Selection Type is valid and all preceding steps are visited.
                    btn.Valid = self.SelectionTypeInfo.valid && !self.AllSteps.filter((s, sidx) => sidx < idx && !s.Visited).length;
                    break;
                case 2: // Dates. Valid once Recipient is valid and all preceding steps are visited and valid.
                    btn.Valid =
                        self.SelectionTypeInfo.valid &&
                        self.RecipientInfo.valid &&
                        !self.AllSteps.filter((s, sidx) => sidx < idx && !s.Visited).length;
                    break;
                case 3: // Categories. Valid once Recipient and Dates are valid, and all preceding steps are visited and valid.
                    btn.Valid =
                        self.SelectionTypeInfo.valid &&
                        self.RecipientInfo.valid &&
                        self.DatesInfo.valid &&
                        !self.AllSteps.filter((s, sidx) => sidx < idx && !s.Visited).length;
                    break;
                case 4: // Review. Valid once categories are valid and all preceding steps are visited and valid.
                    btn.Valid =
                        self.SelectionTypeInfo.valid &&
                        self.RecipientInfo.valid &&
                        self.DatesInfo.valid &&
                        self.CategoriesInfo.valid &&
                        !self.AllSteps.filter((s, sidx) => sidx < idx && !s.Visited).length;
                    break;
            }

            btn.Active = idx === curStep;

            if (!btn.Visited && idx === curStep) {
                btn.Visited = true;
            }
        });
    }

    // #endregion

    OKHandler() {
        const self = this;

        const formval = self.SearchForm.value;

        this.dialogRef.close({
            User: formval.RecipientInfo.User,
            DateBegin: formval.DatesInfo.DateBegin,
            DateEnd: formval.DatesInfo.DateEnd,
            Active: formval.DatesInfo.Active,
            Categories: formval.CategoriesInfo.filter(c => c.Selected === true),
        } as BaseOOODesigneeSearchResult);
    }

    HandleUserSearch() {
        const self = this;

        const form = self.SearchParams.value;

        // There must be at least 1 of either first name, last name, or SAPID provided.
        // We will not allow a search on 300000+ users without something!
        if (!form.FirstName && !form.LastName && !form.SAPID) {
            self.splashSvc.SnackbarAlert('You must provide at least one search option in order to conduct a search');
            return;
        }

        // Pad SAPID up to 8 characters, if it is provided.
        if (form.SAPID && form.SAPID.length < 8) {
            form.SAPID = ('00000000' + form.SAPID).slice(-8);
        }

        // Perform the search.
        self.http
            .post<UserSearchResult[]>(this.configSvc.hostAddress + `Api/Services/IRIS/SearchForUser`, {
                AuthToken: self.userSvc.User.AuthToken || '',
                CurrentRole: self.userSvc.User.CurrentRole || '',
                FirstName: form.FirstName || '',
                LastName: form.LastName || '',
                SAPID: form.SAPID || '',
            })
            .subscribe(
                users => {
                    // Update the search results
                    self.SearchResults = users;
                    self.NoData = !users || !users.length;
                    self.ResultsData.data = self.NoData ? [<UserSearchResult>{}] : users;
                    self.UserSearchConducted = true;

                    self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllDistrictHeaders.slice();
                    self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();

                    // If on a small display, scroll the scroll panel all the way downward
                    if (self.SmallDisplay) {
                        window.setTimeout(() => {
                            // const maxScroll = (<HTMLDivElement>self.ScrollPanel.nativeElement).scrollHeight;

                            // (<HTMLDivElement>self.ScrollPanel.nativeElement).scrollTop = maxScroll;
                            if (self.ScrollPanel) {
                                (self.ScrollPanel.nativeElement as HTMLDivElement).scrollIntoView(true);
                            }
                        }, 0);
                    }
                },
                error => {
                    const msg = self.errorSvc.RetrieveServiceErrorMessage(error);

                    self.splashSvc.SnackbarError(msg, 5000);
                }
            );
    }

    ResetForm() {
        this.SearchParams.reset();
        if (this.LastnameInput) {
            (<HTMLInputElement>this.LastnameInput.nativeElement).focus();
        }
    }

    SelectAllCategories() {
        const self = this;

        for (let i = 0; i < self.CategoriesInfo.length; ++i) {
            const entry = self.CategoriesInfo.at(i);

            entry.get('Selected').setValue(true, { emitEvent: false });
        }

        self.CategoriesInfo.updateValueAndValidity();
    }

    SelectUser(user: UserSearchResult) {
        const self = this;

        if (!user) {
            return;
        }

        self.RecipientInfo.get('User').setValue(user);
    }

    // #region Set Current Step

    SetCurrentStep(step: number) {
        const self = this;

        if (step == null || step < 0 || step >= self.AllSteps.length) {
            return;
        }

        const curStep = self.CurrentStep.value;

        // Update the current step.
        self.CurrentStep && self.CurrentStep.setValue(step);

        let focusProc = null;

        switch (step) {
            case 1:
                // If this is the district search type, we need to focus on the last name field
                if (self.SelectionTypeInfo.get('Type').value === 'district') {
                    focusProc = self.LastnameInput && self.LastnameInput.nativeElement.focus.bind(self.LastnameInput.nativeElement);
                }
                break;
            case 2:
                // For the date, we need to focus on the date begin field
                focusProc = self.DateBeginInput && self.DateBeginInput.nativeElement.focus.bind(self.DateBeginInput.nativeElement);
                break;
        }

        if (focusProc) {
            window.setTimeout(() => {
                focusProc();
            }, 0);
        }
    }

    // #endregion

    // #region Unselect All Categories

    UnselectAllCategories() {
        const self = this;

        for (let i = 0; i < self.CategoriesInfo.length; ++i) {
            const entry = self.CategoriesInfo.at(i);

            entry.get('Selected').setValue(false, { emitEvent: false });
        }

        self.CategoriesInfo.updateValueAndValidity();
    }

    // #endregion

    // #region Update Step

    UpdateStep(dir: number) {
        const self = this;
        const curStep = self.CurrentStep.value;

        if ((dir < 0 && curStep === 0) || (dir > 0 && curStep === self.AllSteps.length - 1)) {
            return;
        }

        const nextStep = curStep + dir;

        self.SetCurrentStep(nextStep);
    }

    // #endregion
}

export class BaseOOODesigneeSearchDialogOptions extends BaseDialogOptions {
    Categories?: CategoryInfo[];
    Subordinates?: PersonInfo[];
    InitialValues?: OutOfOfficeSetting;
}

export class BaseOOODesigneeSearchAcceptOption {
    DisplayText: string;
    ReturnCode: string;
    Color?: string;
}

export class BaseOOODesigneeSearchResult {
    Categories: CategoryInfo[];
    DateBegin: Date;
    DateEnd: Date;
    Active: boolean;
    User: UserSearchResult;

    ReturnCode: string;
}

// #region Step Button Info Class

class StepButtonInfo {
    Text?: string;
    Active?: boolean;
    Valid?: boolean;
    Visited?: boolean;
}

// #endregion
